import React, {useEffect, useMemo, useState} from "react";
import {Sidebar} from "primereact/sidebar";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Checkbox} from "primereact/checkbox";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {RichTextEditor, RichTextView} from "../../../system/components/rich-text";
import {DECISION_STATUS, TASK_STATUS, doneShare, errorText, faDate, faNum, faShortDate, plainOf, sameTitle} from "./meetings-labels";
import {Chain, DayPicker, Progress, SectionHead, StatusPill, personOf} from "./meetings-ui";
import {milestoneOptions, personOptions, targetFromValue, targetOptions} from "./output-dialog";

// «جزئیات تصمیم» (spec 5.8, 5.9): owner, due date, status and goal / milestone; the project tasks that carry it out
// (new or linked, completed with the «تکمیل» tick); blocker; result and its verification; follow-up history.

const drawerPt = {root: {style: {width: "min(46rem, 100vw)"}}, content: {className: "p-0"}};
const STATUS_OPTIONS = Object.entries(DECISION_STATUS).map(([value, entry]) => ({value, label: entry.label}));
const OPEN_TASK_STATUSES = ["todo", "doing", "blocked"].map((value) => ({value, label: TASK_STATUS[value].label}));

export default function DecisionDrawer({decision, tab, visible, onHide, focusTask, actions}) {
    const [editingMeta, setEditingMeta] = useState(false);
    const [meta, setMeta] = useState({});
    const [blocker, setBlocker] = useState("");
    const [result, setResult] = useState("");
    const [verified, setVerified] = useState(false);
    const [note, setNote] = useState("");
    const [taskForm, setTaskForm] = useState(null);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!decision) return;
        setBlocker(decision.blocker);
        setResult(decision.result);
        setVerified(decision.resultVerified);
        setMeta({title: decision.title, description: decision.description, owner: decision.owner, dueDate: decision.dueDate, status: decision.status, target: decision.target ? `${decision.target.type}:${decision.target.id}` : ""});
        setError("");
    }, [decision]);
    useEffect(() => {
        setEditingMeta(false);
        setTaskForm(null);
        setNote("");
    }, [decision?.key]);
    useEffect(() => {
        if (!visible || !focusTask) return;
        const timer = window.setTimeout(() => document.getElementById(`mt-task-${focusTask}`)?.scrollIntoView({behavior: "smooth", block: "center"}), 250);
        return () => window.clearTimeout(timer);
    }, [visible, focusTask]);

    const people = useMemo(() => personOptions(tab.people, tab.members), [tab]);
    const tasks = useMemo(() => (decision ? decision.taskKeys.map((key) => tab.tasks.find((task) => task.key === key)).filter(Boolean) : []), [decision, tab.tasks]);
    if (!decision) return null;

    const meeting = tab.meetings.find((item) => item.key === decision.meetingKey);
    const followUps = tab.meetings.filter((item) => decision.followUpKeys.includes(item.key));
    const target = decision.target ? tab.targets.find((item) => item.type === decision.target.type && item.id === decision.target.id) : null;
    const owner = personOf(tab, decision.owner);
    const progress = doneShare(tasks);
    const allDone = tasks.length > 0 && tasks.every((task) => task.status === "done");
    const canUpdate = decision.canUpdate;
    const canEditMeta = tab.permissions.canEdit;

    const run = async (name, work) => {
        setBusy(name);
        setError("");
        try {
            await work();
            return true;
        } catch (requestError) {
            setError(errorText(requestError));
            return false;
        } finally {
            setBusy("");
        }
    };
    const update = (patch) => run("decision", () => actions.updateDecision(decision.key, patch));
    const saveMeta = async () => {
        if (!meta.title?.trim()) return setError("عنوان تصمیم را بنویسید.");
        const ok = await update({title: meta.title, description: meta.description, owner: meta.owner || null, dueDate: meta.dueDate || null, status: meta.status, target: targetFromValue(meta.target)});
        if (ok) setEditingMeta(false);
    };

    return (
        <Sidebar visible={visible} position="left" onHide={onHide} className="rtl" pt={drawerPt}
                 header={<div><b className="text-sm">جزئیات تصمیم</b><p className="pd-sub">{`ثبت‌شده در ${faDate(decision.createdAt)}`}</p></div>}>
            <div className="p-4 flex flex-column gap-3" dir="rtl">
                <div className="pd-card pd-card-pad flex flex-column gap-2">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <span className="pd-tiny text-500">تصمیم مستقل</span>
                        <StatusPill kind="decision" status={decision.status}/>
                        <span className="flex-1"/>
                        {canEditMeta && <button type="button" className="pd-btn small" onClick={() => setEditingMeta((current) => !current)}><Icon icon="mi mi:edit" className="text-sm"/>ویرایش تصمیم، مسئول و موعد</button>}
                    </div>
                    <h2 className="text-base font-bold m-0">{decision.title}</h2>
                    <RichTextView value={decision.description} className="text-xs text-700"/>
                    <div className="mt-meta small">
                        <span><Icon icon="mi mi:event"/>جلسه مبدأ</span>
                        {meeting ? <button type="button" className="pd-link pd-tiny" onClick={() => actions.openMeeting(meeting.key)}>{`${meeting.title} · ${faDate(meeting.date)}`}</button> : <span>ثبت نشده</span>}
                    </div>
                    {followUps.length > 0 && (
                        <div className="mt-meta small">
                            <span><Icon icon="mi mi:event_repeat"/>پیگیری دوباره در</span>
                            {followUps.map((item) => <button key={item.key} type="button" className="pd-link pd-tiny" onClick={() => actions.openMeeting(item.key)}>{item.title}</button>)}
                        </div>
                    )}
                    {canUpdate && !editingMeta && (
                        <div className="flex align-items-center gap-2 flex-wrap mt-1">
                            <span className="pd-tiny text-500">وضعیت تصمیم:</span>
                            <Dropdown value={decision.status} options={STATUS_OPTIONS} disabled={Boolean(busy)} onChange={(event) => update({status: event.value})} className="text-xs mt-compact" panelClassName="rtl text-xs"/>
                        </div>
                    )}
                </div>

                {editingMeta && (
                    <section className="mt-form-section">
                        <label className="mt-field">عنوان تصمیم
                            <InputText value={meta.title || ""} onChange={(event) => setMeta({...meta, title: event.target.value})} className="w-full text-xs" maxLength={200}/>
                        </label>
                        <div className="mt-field">شرح یا معیار موفقیت
                            <RichTextEditor value={meta.description || ""} onChange={(value) => setMeta((current) => ({...current, description: value}))} minHeight="4rem" ariaLabel="شرح تصمیم"/>
                        </div>
                        <div className="mt-form-grid">
                            <label className="mt-field">مسئول پیگیری
                                <Dropdown value={meta.owner} options={people} onChange={(event) => setMeta({...meta, owner: event.value})} filter showClear className="w-full text-xs" panelClassName="rtl text-xs"/>
                            </label>
                            <label className="mt-field">موعد اجرا
                                <DayPicker value={meta.dueDate} onChange={(day) => setMeta({...meta, dueDate: day})}/>
                            </label>
                            <label className="mt-field">وضعیت تصمیم
                                <Dropdown value={meta.status} options={STATUS_OPTIONS} onChange={(event) => setMeta({...meta, status: event.value})} className="w-full text-xs" panelClassName="rtl text-xs"/>
                            </label>
                            <label className="mt-field">هدف یا مایلستون
                                <Dropdown value={meta.target || null} options={targetOptions(tab.targets)} onChange={(event) => setMeta({...meta, target: event.value || ""})} filter showClear placeholder="بدون پیوند" emptyMessage="هدف یا مایلستونی برای این پروژه ثبت نشده" className="w-full text-xs" panelClassName="rtl text-xs"/>
                            </label>
                        </div>
                        <p className="pd-tiny text-500 m-0">هدف یا مایلستون تصمیم فقط مرجع است و در پیشرفت مایلستون حساب نمی‌شود؛ برای اثر در پیشرفت، کارهای تصمیم را به مایلستون وصل کنید.</p>
                        <div className="flex justify-content-end gap-2">
                            <button type="button" className="pd-btn small" onClick={() => setEditingMeta(false)}>انصراف</button>
                            <button type="button" className="pd-btn small primary" disabled={Boolean(busy)} onClick={saveMeta}><Icon icon="mi mi:save" className="text-sm"/>ذخیره تغییرات</button>
                        </div>
                    </section>
                )}

                <div className="mt-summary-boxes">
                    <div>
                        {owner?.member ? <Avatar memberData={owner.member} size="large"/> : <span className="pd-icon-box"><Icon icon="mi mi:person"/></span>}
                        <span><small>مسئول پیگیری</small><b>{owner?.name || "تعیین نشده"}</b></span>
                    </div>
                    <div>
                        <span className="pd-icon-box"><Icon icon="mi mi:event"/></span>
                        <span><small>موعد اجرا</small><b>{faDate(decision.dueDate)}</b></span>
                    </div>
                    <div>
                        <span className="pd-icon-box"><Icon icon="mi mi:flag"/></span>
                        <span><small>{target ? (target.type === "goal" ? "هدف" : "مایلستون") : "هدف / مایلستون"}</small><b>{target?.title || "تعیین نشده"}</b></span>
                    </div>
                </div>

                {decision.blocker && (
                    <div className={`mt-blocker big ${decision.blockerStatus === "resolved" ? "is-resolved" : ""}`}>
                        <Icon icon={decision.blockerStatus === "resolved" ? "mi mi:check_circle" : "mi mi:block"}/>
                        <div className="flex-1">
                            <span className="flex align-items-center gap-2 flex-wrap">
                                <b className="text-xs">مانع ثبت‌شده</b>
                                <span className={`pd-pill tone-${decision.blockerStatus === "resolved" ? "green" : "red"}`}>{decision.blockerStatus === "resolved" ? "حل شده" : "باز"}</span>
                                {decision.blockerStatus === "resolved" && decision.blockerResolvedAt && <span className="pd-tiny text-500">{faDate(decision.blockerResolvedAt)}</span>}
                            </span>
                            <span className="text-xs block mt-1">{decision.blocker}</span>
                        </div>
                        {canUpdate && (decision.blockerStatus === "resolved"
                            ? <button type="button" className="pd-btn small" disabled={Boolean(busy)} onClick={() => update({blockerStatus: "open"})}>بازگشایی</button>
                            : <button type="button" className="pd-btn small primary" disabled={Boolean(busy)} onClick={() => update({blockerStatus: "resolved"})}><Icon icon="mi mi:check" className="text-sm"/>مانع حل شد</button>)}
                    </div>
                )}

                <section className="pd-card pd-card-pad">
                    <SectionHead icon="route" title="زنجیره‌ی این تصمیم" sub="ردیابی از جلسه تا نتیجه"/>
                    <Chain items={[
                        {label: "جلسه", done: true},
                        {label: "تصمیم", done: true},
                        {label: tasks.length ? `کار اجرایی (${faNum(tasks.length)})` : "کار اجرایی", done: allDone},
                        {label: decision.resultVerified ? "نتیجه تأییدشده" : "نتیجه", done: decision.resultVerified},
                        {label: target ? (target.type === "goal" ? "هدف" : "مایلستون") : "هدف/مایلستون", active: Boolean(target)},
                    ]}/>
                </section>

                {/* tasks */}
                <section className="pd-card pd-card-pad">
                    <SectionHead icon="task_alt" title="کارهای مرتبط زوفینو" sub="کار موجود را پیوند دهید تا نسخه‌ی تکراری ساخته نشود."
                                 action={canUpdate && (
                                     <div className="flex gap-2">
                                         {tab.permissions.canCreateTask && <button type="button" className="pd-btn small" onClick={() => setTaskForm(taskForm?.mode === "new" ? null : {mode: "new", title: "", assignee: decision.owner || "", dueDate: decision.dueDate || "", milestones: decision.target?.type === "milestone" ? [decision.target.id] : []})}><Icon icon="mi mi:add" className="text-sm"/>ایجاد کار</button>}
                                         <button type="button" className="pd-btn small" disabled={tab.tasks.every((task) => decision.taskKeys.includes(task.key))} onClick={() => setTaskForm(taskForm?.mode === "existing" ? null : {mode: "existing", task: ""})}><Icon icon="mi mi:link" className="text-sm"/>پیوند کار موجود</button>
                                     </div>
                                 )}/>
                    {taskForm && <TaskForm form={taskForm} setForm={setTaskForm} tab={tab} decision={decision} people={people} busy={busy}
                                           onSubmit={(payload) => run("task", async () => {await actions.addTask(decision.key, payload); setTaskForm(null);})}/>}
                    {tasks.length ? (
                        <div className="flex flex-column gap-2">
                            {tasks.map((task) => <TaskEditor key={task.key} task={task} tab={tab} decision={decision} people={people} focused={focusTask === task.key} actions={actions} run={run} busy={busy}/>)}
                            <div className="mt-rollup">
                                <span className="text-xs">کارهای انجام‌شده</span>
                                <Progress value={progress}/>
                            </div>
                            {allDone && !decision.resultVerified && <p className="mt-warning m-0"><Icon icon="mi mi:info" className="text-xs"/> همه‌ی کارها تکمیل شده‌اند؛ اما نتیجه‌ی تصمیم هنوز تأیید نشده است.</p>}
                        </div>
                    ) : <p className="pd-tiny text-500 m-0">هنوز کاری به این تصمیم وصل نیست. می‌توانید کار موجود را پیوند دهید یا کار تازه بسازید.</p>}
                </section>

                {/* blocker and result */}
                <div className="mt-two-col">
                    <section className="pd-card pd-card-pad flex flex-column gap-2" id="mt-blocker-editor">
                        <SectionHead icon="block" title="مانع و وابستگی" sub="مسئول تصمیم برای حل آن اعلان می‌گیرد"/>
                        <InputTextarea value={blocker} disabled={!canUpdate} onChange={(event) => setBlocker(event.target.value)} rows={3} autoResize placeholder="چه چیزی جلوی پیشرفت را گرفته؟" className="w-full text-xs" maxLength={2000}/>
                        {canUpdate && (
                            <div className="flex align-items-center gap-2">
                                <button type="button" className="pd-btn small" disabled={Boolean(busy) || blocker.trim() === decision.blocker} onClick={() => update({blocker})}><Icon icon="mi mi:save" className="text-sm"/>{!blocker.trim() && decision.blocker ? "حذف مانع" : "ثبت مانع"}</button>
                                <span className="pd-tiny text-500">هر تغییر در سابقه می‌ماند.</span>
                            </div>
                        )}
                    </section>
                    <section className="pd-card pd-card-pad flex flex-column gap-2">
                        <SectionHead icon="flag" title="نتیجه‌ی تصمیم" sub="نتیجه با انجام کار یکی نیست"/>
                        {canUpdate
                            ? <RichTextEditor value={result} minHeight="4.5rem" placeholder="بعد از اجرا چه تغییری واقعاً رخ داد؟ شاهد یا سند نتیجه چیست؟" ariaLabel="نتیجه‌ی تصمیم"
                                              onChange={(value) => {setResult(value); if (value !== decision.result) setVerified(false);}}/>
                            : <RichTextView value={decision.result} className="text-xs" empty={<p className="pd-tiny text-500 m-0">نتیجه هنوز ثبت نشده است.</p>}/>}
                        <label className="flex align-items-center gap-2 text-xs">
                            <Checkbox checked={verified} disabled={!canUpdate || !plainOf(result)} onChange={(event) => setVerified(event.checked)}/>
                            <span>نتیجه‌ی مورد انتظار حاصل شد و بررسی شده است</span>
                        </label>
                        {canUpdate && (
                            <div className="flex align-items-center gap-2">
                                <button type="button" className="pd-btn small primary" disabled={Boolean(busy) || !plainOf(result) || (result === decision.result && verified === decision.resultVerified)}
                                        onClick={() => update({result, resultVerified: verified})}><Icon icon="mi mi:done_all" className="text-sm"/>ثبت نتیجه</button>
                                <span className="pd-tiny text-500">{decision.resultVerified ? `تأیید شده در ${faDate(decision.resultVerifiedAt)}` : "برای تأیید، ابتدا نتیجه را بنویسید."}</span>
                            </div>
                        )}
                    </section>
                </div>

                {/* history */}
                <section className="pd-card pd-card-pad flex flex-column gap-2">
                    <SectionHead icon="history" title="پیگیری و سابقه تغییرات" sub="چه چیزی، چه زمانی و توسط چه کسی به‌روزرسانی شده است؟"/>
                    {canUpdate && (
                        <div className="flex gap-2 align-items-start">
                            <InputTextarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} autoResize placeholder="به‌روزرسانی یا توضیح پیگیری را بنویسید…" className="flex-1 text-xs" maxLength={2000}/>
                            <button type="button" className="pd-btn small primary" disabled={Boolean(busy) || !note.trim()}
                                    onClick={() => run("note", async () => {await actions.addNote(decision.key, note.trim()); setNote("");})}>ثبت پیگیری</button>
                        </div>
                    )}
                    <div className="pd-timeline">
                        {[...decision.history].reverse().map((entry) => (
                            <div key={entry.id} className="pd-timeline-item">
                                <span className="pd-timeline-dot"/>
                                <p className="text-xs m-0">{entry.text}</p>
                                <span className="pd-tiny text-500">{`${entry.author?.name || ""} · ${faShortDate(entry.at)}`}</span>
                            </div>
                        ))}
                    </div>
                </section>
                {error && <p className="text-xs tone-red m-0" role="alert">{error}</p>}
                <p className="pd-tiny text-500 m-0"><Icon icon="mi mi:link" className="text-xs"/> تکمیل کارها ممکن است شرط لازم باشد، اما به‌تنهایی تحقق نتیجه را اثبات نمی‌کند.</p>
            </div>
        </Sidebar>
    );
}

function TaskForm({form, setForm, tab, decision, people, busy, onSubmit}) {
    const available = tab.tasks.filter((task) => !decision.taskKeys.includes(task.key));
    const match = form.mode === "new" && form.title.trim() ? tab.tasks.find((task) => sameTitle(task.title, form.title)) : null;
    const submit = () => {
        if (form.mode === "existing") return form.task && onSubmit({mode: "existing", task: form.task});
        if (!form.title.trim()) return;
        // a task with the same title is linked, not copied
        return onSubmit(match ? {mode: "existing", task: match.key} : {mode: "new", title: form.title.trim(), assignee: form.assignee || null, dueDate: form.dueDate || null, milestones: form.milestones || []});
    };
    return (
        <div className="mt-form-section mb-2">
            <div className="flex align-items-center justify-content-between">
                <b className="text-xs">{form.mode === "new" ? "کار تازه برای همین تصمیم" : "پیوند به کار موجود پروژه"}</b>
                <button type="button" className="mt-icon-btn" aria-label="بستن" onClick={() => setForm(null)}><Icon icon="mi mi:close" className="text-sm"/></button>
            </div>
            {form.mode === "new" ? (
                <>
                    <InputText autoFocus value={form.title} onChange={(event) => setForm({...form, title: event.target.value})} placeholder="مثلاً هماهنگی با تیم زیرساخت" className="w-full text-xs" maxLength={200}/>
                    {match && <p className="mt-warning m-0">{decision.taskKeys.includes(match.key) ? "این کار از قبل به همین تصمیم وصل است؛ نسخه‌ی تکراری ساخته نمی‌شود." : "این کار با همین عنوان از قبل در پروژه هست؛ ثبت آن همان کار را پیوند می‌دهد."}</p>}
                    <div className="mt-form-grid">
                        <label className="mt-field">مسئول کار
                            <Dropdown value={form.assignee} options={people} onChange={(event) => setForm({...form, assignee: event.value})} filter showClear className="w-full text-xs" panelClassName="rtl text-xs"/>
                        </label>
                        <label className="mt-field">موعد
                            <DayPicker value={form.dueDate} onChange={(day) => setForm({...form, dueDate: day})}/>
                        </label>
                    </div>
                    <label className="mt-field">مایلستون کار
                        <MultiSelect value={form.milestones || []} options={milestoneOptions(tab.targets)} onChange={(event) => setForm({...form, milestones: event.value})} placeholder="بدون مایلستون" display="chip" className="w-full text-xs" panelClassName="rtl text-xs" emptyMessage="مایلستونی برای این پروژه ثبت نشده"/>
                    </label>
                </>
            ) : (
                <>
                    <Dropdown value={form.task} options={available.map((task) => ({value: task.key, label: `${task.title} · ${personOf(tab, task.assignee)?.name || "بدون مسئول"}`}))} onChange={(event) => setForm({...form, task: event.value})}
                              filter placeholder="یک کار را انتخاب کنید…" className="w-full text-xs" panelClassName="rtl text-xs"/>
                    <p className="pd-tiny text-500 m-0">همان کار با مسئول و وضعیت فعلی‌اش وصل می‌شود؛ مورد تکراری ساخته نمی‌شود.</p>
                </>
            )}
            <div className="flex justify-content-end gap-2">
                <button type="button" className="pd-btn small" onClick={() => setForm(null)}>انصراف</button>
                <button type="button" className="pd-btn small primary" disabled={Boolean(busy) || (form.mode === "new" ? !form.title.trim() : !form.task)} onClick={submit}>
                    {form.mode === "existing" || match ? "پیوند کار" : "افزودن به کارهای پروژه"}
                </button>
            </div>
        </div>
    );
}

function TaskEditor({task, tab, decision, people, focused, actions, run, busy}) {
    const [open, setOpen] = useState(focused);
    useEffect(() => { if (focused) setOpen(true); }, [focused]);
    const others = tab.decisions.filter((item) => item.key !== decision.key && item.taskKeys.includes(task.key));
    const canStatus = tab.permissions.canChangeTaskStatus || task.assignee === tab.permissions.meKey;
    const canEdit = tab.permissions.canEditTask;
    const patch = (value) => run(`task-${task.key}`, () => actions.updateTask(task.key, value));
    const done = task.status === "done";
    return (
        <article id={`mt-task-${task.key}`} className={`mt-task ${focused ? "is-focused" : ""}`}>
            <div className="flex align-items-center gap-2">
                <button type="button" className="mt-icon-btn" aria-expanded={open} aria-label={open ? "بستن جزئیات کار" : "جزئیات کار"} onClick={() => setOpen(!open)}>
                    <Icon icon={open ? "mi mi:expand_less" : "mi mi:expand_more"} className="text-sm"/>
                </button>
                <span className="flex-1 min-w-0">
                    <b className={`text-xs block text-overflow-ellipsis white-space-nowrap overflow-hidden ${done ? "line-through text-500" : ""}`}>{task.title}</b>
                    <span className="pd-tiny text-500">{`مسئول: ${personOf(tab, task.assignee)?.name || "بدون مسئول"} · موعد: ${faShortDate(task.dueDate)}`}</span>
                </span>
                {task.checklist?.total > 0 && <span className="pd-tiny text-500">{`چک‌لیست ${faNum(task.checklist.done)} از ${faNum(task.checklist.total)}`}</span>}
                {!done && <Dropdown value={task.status} options={OPEN_TASK_STATUSES} disabled={!canStatus || Boolean(busy)} onChange={(event) => patch({status: event.value})}
                                    className="text-xs mt-compact" panelClassName="rtl text-xs" aria-label={`وضعیت کار: ${task.title}`}/>}
                <label className={`mt-complete ${done ? "is-done" : ""}`}>
                    <Checkbox checked={done} disabled={!canStatus || Boolean(busy)} onChange={(event) => patch({done: event.checked})} aria-label={`تکمیل کار: ${task.title}`}/>
                    <span>{done ? "تکمیل شده" : "تکمیل"}</span>
                </label>
            </div>
            {open && (
                <div className="mt-task-details">
                    <div className="mt-form-grid">
                        <label className="mt-field">مسئول کار
                            <Dropdown value={task.assignee || null} options={people} disabled={!canEdit || Boolean(busy)} onChange={(event) => patch({assignee: event.value || null})} filter showClear className="w-full text-xs" panelClassName="rtl text-xs"/>
                        </label>
                        <label className="mt-field">موعد
                            <DayPicker value={task.dueDate} disabled={!canEdit || Boolean(busy)} onChange={(day) => patch({dueDate: day})}/>
                        </label>
                    </div>
                    <label className="mt-field">مایلستون کار
                        <MultiSelect value={task.milestones || []} options={milestoneOptions(tab.targets)} disabled={!canEdit || Boolean(busy)} onChange={(event) => patch({milestones: event.value})}
                                     placeholder="بدون مایلستون" display="chip" className="w-full text-xs" panelClassName="rtl text-xs" emptyMessage="مایلستونی برای این پروژه ثبت نشده"/>
                    </label>
                    <span className="pd-tiny text-500">کار اصلی پروژه — همان کار در فهرست و برد پروژه است؛ تغییر آن همه‌جا دیده می‌شود.</span>
                    {others.length > 0 && (
                        <div className="flex align-items-center gap-2 flex-wrap pd-tiny">
                            <span className="text-500">همچنین به تصمیم:</span>
                            {others.map((item) => <button key={item.key} type="button" className="pd-link pd-tiny" onClick={() => actions.openDecision(item.key)}>{item.title}</button>)}
                        </div>
                    )}
                </div>
            )}
        </article>
    );
}
